import {INextHistoryState, TBeforePopStateCallback} from 'core/types';
import Router from 'next/router';
import {useCallback, useEffect, useState} from 'react';
import {isUrlActuallyChange} from 'core/helpers';
import {onHistoryChange} from 'core/next/helpers';

interface IPageLoadingOptions {
    resolver?: (url: string, options: INextHistoryState['options']) => boolean;
    watchShallow?: boolean;
}

export const usePageLoading = ({resolver, watchShallow}: IPageLoadingOptions = {}): boolean => {
    const [isLoading, setIsLoading] = useState(false);

    const handleRouterChangeStart = useCallback(
        (newUrl: string, options: INextHistoryState['options']) => {
            if (options.shallow && !watchShallow) {
                return;
            }

            if (!isUrlActuallyChange(newUrl)) {
                return;
            }

            if (resolver) {
                setIsLoading(resolver(newUrl, options));
                return;
            }

            setIsLoading(true);
        },
        [resolver, watchShallow],
    );

    const handleRouterBack = useCallback<TBeforePopStateCallback>(() => {
        setIsLoading(true);
        return true;
    }, []);

    const handleRouteChangeComplete = useCallback(() => {
        setIsLoading(false);
    }, []);

    const handleRouteChangeError = useCallback(() => {
        setIsLoading(false);
    }, []);

    useEffect(() => {
        const disposer = onHistoryChange(handleRouterBack);

        Router.events.on('routeChangeStart', handleRouterChangeStart);
        Router.events.on('routeChangeComplete', handleRouteChangeComplete);
        Router.events.on('routeChangeError', handleRouteChangeError);

        return () => {
            Router.events.off('routeChangeStart', handleRouterChangeStart);
            Router.events.off('routeChangeComplete', handleRouteChangeComplete);
            Router.events.off('routeChangeError', handleRouteChangeError);

            disposer(handleRouterBack);
        };
    }, [handleRouterBack, handleRouterChangeStart, handleRouteChangeComplete, handleRouteChangeError]);

    return isLoading;
};
